import React, {useEffect, useMemo, useState} from 'react';
import ReactGA from 'react-ga4';
import {compose} from 'redux';
import {connect} from 'react-redux';
import TagManager from 'react-gtm-module';

import './App.scss';
import Routes from './util/routes';
import {
  addToGlobalStyles,
  setStrings,
  setStringsDefault,
  setBuDefaultFunctionalities,
  setGlobalStyles,
  setTenant,
  setBuDefaultStyles,
  setLanguage,
  setBankAccountDataHolder,
  setIban,
  setTitular,
  setBusFuncMapDefault,
  setIsUnderMaintenance,
  setIsUnderMaintenanceMessage,
} from './store/actions';
import {AuthProvider} from './providers/AuthProvider';
import {
  uriMemberData,
  uriGetFunctionalities,
  uriGetTenants,
  uriGetTitular,
} from './api/endpoints';
import {get} from './api/api';
import AppSpinner from './components/app-spinner';
import {RedirectUrlProvider} from './contexts/RedirectUrlContext';
import {
  isObjectEmpty,
  isRegExpValid,
  updateStringFeatureWithLabels,
} from './util/utility';
import {functionalitiesAliasMap} from './util/strings';
import {getBusInfo} from './api/client';
import PageMaintenance from './components/page-maintenance';
import getValuesToStoreWithBuDataFromGraphql from './graphQL';
import {Store} from './store';

export interface ITenant {
  alias: string;
  alternativeRegistrationURL: string;
  buDefaultAlias: string;
  country: string;
  countryDescription: string;
  footerImage: string;
  id: number;
  languages: any;
  loginFlow: any;
  logoLoginImage: string;
  registerSuccessImage: string;
  spinnerAppGreyBackground: string;
  spinnerAppWhiteBackground: string;
  spinnerPortalGreyBackground: string;
  spinnerPortalWhiteBackground: string;
  title: string;
  vatNumberValidator: string;
  mainLanguageAlias: string;
}

export interface AppProps {
  globalStyles: any;
  addToGlobalStyles(value: any): void;
  setGlobalStyles(value: any): void;
  strings: any;
  selectedBu: any;
  busStylesMap: any;
  buStyles: any;
  user: any;
  loadingInfo: boolean;
  setTenant(value: any): void;
  setLanguage(value: any): void;
  setBankAccountDataHolder(value: any): void;
  setIban(value: any): void;
  setTitular(value: any): void;
  bankAccountDataHolder: any;
  updateRoutesNewSelectedPatient: any;
  busFuncMap: any;
  busFuncMapDefault: any;
  tenant: ITenant;
  language: any;
  setBusFuncMapDefault: Function;
  reduxStrings: any;
  setStrings: Function;
  setStringsDefault: Function;
  setBuDefaultFunctionalities: Function;
  setIsUnderMaintenance: Function;
  setIsUnderMaintenanceMessage: Function;
  isUnderMaintenance: boolean;
  busMap: any;
}

function App({
  strings,
  globalStyles,
  selectedBu,
  user,
  loadingInfo,
  setTenant,
  setLanguage,
  setBankAccountDataHolder,
  setIban,
  setTitular,
  bankAccountDataHolder,
  updateRoutesNewSelectedPatient,
  busFuncMap,
  busFuncMapDefault,
  tenant,
  language,
  setBusFuncMapDefault,
  reduxStrings,
  setStrings,
  setStringsDefault,
  setBuDefaultFunctionalities,
  setIsUnderMaintenance,
  setIsUnderMaintenanceMessage,
  isUnderMaintenance,
  busMap,
}: AppProps) {
  const [localTenant, setLocalTenant] = useState<ITenant>();
  const [googleAnalyticsGtm, setGoogleAnalyticsGtm] = useState(null);
  const [defaultDependencies, setDefaultDependencies] = useState({
    mainLanguageAlias: '',
    language: '',
  });
  const [dependenciesForTtular, setDependenciesForTitular] = useState({
    bankAccountDataHolder: '',
    selectedBuFamId: '',
  });

  const featureName = 'InitializingCard';

  // Clear all cookies when the app loads
  useEffect(() => {
    const clearAllCookies = () => {
      const cookies = document.cookie.split(';');
      const hostname = window.location.hostname;
      const pathname = window.location.pathname;

      // Generate all possible path variations
      const paths = ['/'];
      const pathParts = pathname.split('/').filter((part) => part);
      for (let i = 0; i < pathParts.length; i++) {
        paths.push('/' + pathParts.slice(0, i + 1).join('/'));
      }

      // Generate all possible domain variations
      const domains = ['', hostname];
      if (hostname.indexOf('.') > -1) {
        domains.push('.' + hostname);
        const parts = hostname.split('.');
        for (let i = 1; i < parts.length - 1; i++) {
          domains.push('.' + parts.slice(i).join('.'));
        }
      }

      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i];
        const eqPos = cookie.indexOf('=');
        const name =
          eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();

        if (!name) continue;

        // Try all combinations of domain and path
        for (const domain of domains) {
          for (const path of paths) {
            const domainPart = domain ? `;domain=${domain}` : '';

            // Try with different attribute combinations
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}${domainPart}`;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}${domainPart};secure`;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}${domainPart};samesite=strict`;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}${domainPart};samesite=lax`;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}${domainPart};samesite=none;secure`;
          }
        }
      }
    };

    clearAllCookies();
  }, []);

  const getMaintenanceMessage = async () => {
    const tenantAlias = process.env.REACT_APP_DEFAULT_TENANT_ALIAS;
    await fetch(uriGetTenants(), {
      method: 'GET',
      headers: {
        X_MYADV_API_KEY: tenantAlias || '',
        advBu: tenant?.buDefaultAlias,
        feature: featureName,
      },
    })
      .then(async (res) => {
        const responseText = await res.text();
        const message = JSON.parse(responseText)?.message;
        setIsUnderMaintenanceMessage(message);
      })
      .catch((err) => {
        console.log('err', err);
      });
  };

  const getTenant = async () => {
    await get(uriGetTenants(), false, 0, featureName).then((resp: any) => {
      const result: ITenant = {
        ...(resp?.data?.items || resp)?.[0],
        loginFlow: eval(`(${(resp?.data?.items || resp)?.[0]?.loginFlow})`),
        vatNumberValidator: isRegExpValid(
          (resp?.data?.items || resp)?.[0]?.vatNumberValidator,
        )
          ? (resp?.data?.items || resp)?.[0]?.vatNumberValidator
          : '[\\s\\S]+',
      };
      setLocalTenant(result);
      setTenant(result);
    });
  };

  useEffect(() => {
    setIsUnderMaintenance(null);
    setIsUnderMaintenanceMessage(null);
    getTenant();
  }, []);

  useEffect(() => {
    getMaintenanceMessage();
  }, [isUnderMaintenance]);

  useEffect(() => {
    const isEmptyUser = !user || Object.keys(user).length === 0;

    if (!isEmptyUser && user?.language) {
      setLanguage(user.language);
      return;
    }

    const qsLang = new URLSearchParams(window.location.search).get('language');
    if (isEmptyUser && qsLang) {
      setLanguage(qsLang);
    }
  }, [user]);

  useMemo(() => {
    setDefaultDependencies({
      mainLanguageAlias: tenant?.mainLanguageAlias,
      language: language,
    });
  }, [tenant?.mainLanguageAlias, language]);

  useEffect(() => {
    const getFuncs = async () => {
      await get(
        uriGetFunctionalities(tenant?.buDefaultAlias || 'ADV'),
        false,
        0,
        featureName,
      )
        .then((res: any) => {
          const buDefaultFunctionalities = {
            funcionalidade: res?.data?.items || res,
          };
          let stringsToState = {...strings, ...reduxStrings};
          stringsToState = updateStringFeatureWithLabels(
            stringsToState,
            res?.data?.items || res,
          );
          setStringsDefault({
            ...stringsToState,
            language: language || tenant?.mainLanguageAlias,
          });
          setBuDefaultFunctionalities(buDefaultFunctionalities);
          let busFuncMap: any = {};
          (res?.data?.items || res).map((func: any) => {
            busFuncMap[func?.featureTemplateAlias] = func?.isVisible;
          });
          setBusFuncMapDefault(busFuncMap);
        })

        .catch((err: any) => {
          setStringsDefault(strings);
        });
    };
    if (defaultDependencies?.language && language !== reduxStrings?.language) {
      getFuncs();
    }
  }, [defaultDependencies]);

  // Fetch strings when selectedBu.buId changes
  useEffect(() => {
    const fetchStringsForBu = async () => {
      if (
        !selectedBu?.buId ||
        !tenant ||
        !busMap ||
        selectedBu.buId === 'NOTCLIENT'
      ) {
        return;
      }

      try {
        const result = await getValuesToStoreWithBuDataFromGraphql(
          user?.profile || user,
          selectedBu,
          busMap,
          tenant,
          reduxStrings?.pt?.messages?.default_language || 'pt-PT',
          language,
          featureName,
        );

        if (result?.stringsToStore) {
          setStrings(result.stringsToStore);
        }
      } catch (error) {
        console.error('Error fetching strings for BU:', error);
      }
    };

    fetchStringsForBu();
  }, [selectedBu?.buId]);

  useEffect(() => {
    const googleAnalyticsGtm = busFuncMap?.[selectedBu?.buId]?.find(
      (el: any) =>
        el.featureTemplateAlias === functionalitiesAliasMap.googleanalyticsgtm,
    )?.isVisible;

    setGoogleAnalyticsGtm(googleAnalyticsGtm);
  }, [busFuncMap, selectedBu?.buId]);

  useEffect(() => {
    googleAnalyticsGtm &&
      ReactGA.initialize(process.env.REACT_APP_GOOGLE_ANALYTICS as string);
  }, [googleAnalyticsGtm]);

  useEffect(() => {
    if (googleAnalyticsGtm) {
      const tagManagerArgs = {
        gtmId: process.env.REACT_APP_GOOGLE_TAG_MANAGER as string,
        dataLayer: {
          bu: selectedBu.buId,
        },
      };
      selectedBu.buId && TagManager.initialize(tagManagerArgs);
    }
  }, [selectedBu.buId, googleAnalyticsGtm]);

  useEffect(() => {
    setBankAccountDataHolder(
      strings?.ibanRefunds?.ibanRefunds_bankAccountDataHolder,
    );
  }, [strings, selectedBu?.famId]);

  useEffect(() => {
    const getMemberData = async () => {
      const memberData: any = await get(
        uriMemberData(selectedBu?.buId, selectedBu?.memId, selectedBu?.famId),
        false,
        0,
        featureName,
      );
      if (bankAccountDataHolder == 'INDIVIDUAL') {
        setIban(memberData?.data?.iban);
        setTitular(memberData?.data);
      }
    };

    selectedBu?.memId && getMemberData();
  }, [selectedBu?.memId]);

  useEffect(() => {
    setDependenciesForTitular({
      bankAccountDataHolder: bankAccountDataHolder,
      selectedBuFamId: selectedBu?.famId,
    });
  }, [bankAccountDataHolder, selectedBu?.famId]);

  useEffect(() => {
    const getTitular = async () => {
      try {
        let titular: any = await get(
          uriGetTitular(selectedBu?.buId, selectedBu?.famId),
          false,
          0,
          featureName,
        );
        if (titular?.data) {
          setTitular(titular?.data);
          setIban(titular.iban);
        } else {
          setTitular(null);
          setIban(null);
        }
      } catch (err: any) {
        setTitular(null);
        setIban(null);
      }
    };
    if (bankAccountDataHolder === 'SUBSCRIBER') {
      selectedBu?.famId && getTitular();
    }
  }, [dependenciesForTtular]);

  const chatbot = document.getElementById('example-widget-container');
  if (chatbot) {
    chatbot.style.display = 'none';
    if (user.state === 'CLIENT') {
      chatbot.style.display = 'block';
    }
  }

  const mainContent = () => {
    if (isUnderMaintenance) {
      return (
        <div className="loading-page-container">
          <PageMaintenance />
        </div>
      );
    } else {
      return (
        <div className="app-wrapper">
          {!loadingInfo ? (
            <RedirectUrlProvider>
              <Routes key={updateRoutesNewSelectedPatient} />
            </RedirectUrlProvider>
          ) : (
            <div className="app-spinner-container">
              <AppSpinner className="spinner-content" />
            </div>
          )}
        </div>
      );
    }
  };

  return (
    <AuthProvider>
      <div
        className="App"
        style={{
          ...globalStyles.background,
        }}>
        {mainContent()}
      </div>
    </AuthProvider>
  );
}

const mapStateToProps = ({
  globalStyles,
  strings,
  selectedBu,
  busStylesMap,
  user,
  busMap,
  isAuthenticated,
  selectedBuReady,
  loadingInfo,
  bankAccountDataHolder,
  updateRoutesNewSelectedPatient,
  busFuncMap,
  busFuncMapDefault,
  tenant,
  language,
  stringsDefault,
  isUnderMaintenance,
}: any) => ({
  globalStyles: globalStyles.value,
  strings: strings.value.pt,
  selectedBu: selectedBu.value,
  busStylesMap: busStylesMap.value,
  user: user.value,
  busMap: busMap.value,
  isAuthenticated: isAuthenticated.value,
  buStyles: globalStyles.value,
  selectedBuReady: selectedBuReady.value,
  loadingInfo: loadingInfo.value,
  bankAccountDataHolder: bankAccountDataHolder.value,
  updateRoutesNewSelectedPatient: updateRoutesNewSelectedPatient.value,
  busFuncMap: busFuncMap.value,
  busFuncMapDefault: busFuncMapDefault.value,
  tenant: tenant.value,
  language: language.value,
  reduxStrings: stringsDefault.value,
  isUnderMaintenance: isUnderMaintenance.value,
});

const mapDispatchToProps = (dispatch: any) => ({
  setStrings: (value: any) => dispatch(setStrings(value)),
  setGlobalStyles: (value: any) => dispatch(setGlobalStyles(value)),
  addToGlobalStyles: (value: any) => dispatch(addToGlobalStyles(value)),
  setTenant: (value: any) => dispatch(setTenant(value)),
  setBuDefaultStyles: (value: any) => dispatch(setBuDefaultStyles(value)),
  setLanguage: (value: any) => dispatch(setLanguage(value)),
  setBankAccountDataHolder: (value: any) =>
    dispatch(setBankAccountDataHolder(value)),
  setIban: (value: any) => dispatch(setIban(value)),
  setTitular: (value: any) => dispatch(setTitular(value)),
  setBusFuncMapDefault: (value: any) => dispatch(setBusFuncMapDefault(value)),
  setStringsDefault: (value: any) => dispatch(setStringsDefault(value)),
  setBuDefaultFunctionalities: (value: any) =>
    dispatch(setBuDefaultFunctionalities(value)),
  setIsUnderMaintenance: (value: any) => dispatch(setIsUnderMaintenance(value)),
  setIsUnderMaintenanceMessage: (value: any) =>
    dispatch(setIsUnderMaintenanceMessage(value)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps)(App));
